import React, { useState, useEffect } from "react";

import { matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { Divider, Skeleton, notification, Flex, Button, Col, Form, Grid, Input, Popconfirm, Row, Select } from "antd";
import { useMutation } from "@tanstack/react-query";

import { useForm } from "antd/lib/form/Form";
import { useQuery } from "@tanstack/react-query";
import { useWatchParameterOnRoutes } from "../../Hooks/useGetParameterOnRoutes";
import { useRedirection } from "../../Hooks/useRedirection";
import { useRelativePath } from "../../Hooks/useNextRelativePath";
import { useRequerPermissoes } from "../../Hooks/useRequerPermissoes";

import { Conjunto } from "../../Components/Conjunto";
import { Link } from "react-router-dom";
import { Ordenar } from "../../Utils/Ordenacao";
import { Permissoes } from "../../Constants/Permissoes";
import { PropriedadesDeNotificacao } from "../../Utils/Notificacoes";
import { PropriedadesDoPopConfirm } from "../../Defaults";
import { ResponsiveAntdTable } from "../../Components/ResponsiveAntdTable";
import { Restrito } from "../../Components/Restrito";

import AdicionarAjuda from "../../Components/AdicionarAjuda";
import { TypeAlergia } from "../../Models/Domain/Saude/Alergia";
import { AlergiaService } from "../../Services/Domain/Saude/AlergiaService";
import TextArea from "antd/es/input/TextArea";

export const ListagemDeAlergias: React.FC<{
  aoEditar?: (viagem: TypeAlergia) => any;
  aoAdicionar?: () => any;

  hrefDoBotaoAdicionar?: string;
  hrefDoBotaoEditar?: (viagem: TypeAlergia) => string | void;
}> = (props) => {
  const breakpoints = Grid.useBreakpoint();
  const requerPermissoes = useRequerPermissoes();

  type IFiltro = Parameters<typeof AlergiaService.listar>[0];
  const [filtro, setFiltro] = useState<IFiltro>({
    ativo: true,
  });

  const alergiasDisponiveis = useQuery({
    queryKey: [`AlergiaService.listar`, filtro],
    queryFn: () => AlergiaService.listar(filtro),
  });

  const onFinish = (valores: IFiltro) => {
    setFiltro({
      ...valores,
    });
  };

  const [form] = useForm<IFiltro>();

  const adicionar = requerPermissoes.todas(Permissoes.CriarAlergia);

  return (
      <React.Fragment>
          <Form<IFiltro> form={form} onFinish={onFinish} layout="vertical" autoComplete="off">
              <Row gutter={16}>
                  <Col xs={24} sm={12} xl={5} style={{ order: 1 }}>
                      <Form.Item<IFiltro> name={["nome"]} label="Nome">
                          <Input placeholder="Todos" />
                      </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} xl={11} style={{ order: breakpoints.xl ? 2 : breakpoints.sm ? 3 : 2 }}>
                      <Form.Item<IFiltro> name={["descricao"]} label="Descrição">
                          <Input placeholder="Descrição" />
                      </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} xl={3} style={{ order: breakpoints.xl ? 3 : breakpoints.sm ? 2 : 3 }}>
                      <Form.Item<IFiltro> name="ativo" label="Ativo" initialValue={true}>
                          <Select
                              allowClear
                              placeholder={"Todos"}
                              options={[
                                  { value: true, label: "Ativo" },
                                  { value: false, label: "Inativo" },
                              ]}
                          />
                      </Form.Item>
                  </Col>

                  <Col xs={adicionar ? 12 : 24} xl={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex", order: 4 }}>
                      <Form.Item style={{ width: "100%" }}>
                          <Button type="primary" htmlType="submit" disabled={alergiasDisponiveis.isFetching} style={{ width: "100%" }}>
                              Listar
                          </Button>
                      </Form.Item>
                  </Col>

                  <Restrito aberto={adicionar}>
                      <Col xs={12} xl={3} style={{ alignItems: "end", display: "flex", order: 5 }}>
                          <Form.Item style={{ width: "100%" }}>
                              <Link
                                  to={props.hrefDoBotaoAdicionar ?? "#"}
                                  onClick={(e) => {
                                      !props.hrefDoBotaoAdicionar && e.preventDefault();
                                  }}
                              >
                                  <Button
                                      type="primary"
                                      onClick={() => {
                                          props.aoAdicionar?.();
                                      }}
                                      style={{ width: "100%" }}
                                  >
                                      Adicionar
                                  </Button>
                              </Link>
                          </Form.Item>
                      </Col>
                  </Restrito>
              </Row>
          </Form>

          <Row>
              <Col xs={24}>
                  <ResponsiveAntdTable<TypeAlergia>
                      bordered
                      rowKey={(k) => k.codigo!}
                      locale={{ emptyText: "Nenhuma viagem encontrada." }}
                      loading={alergiasDisponiveis.isFetching}
                      dataSource={alergiasDisponiveis.data?.data}
                      addTitleOn="lg"
                      columns={[
                          {
                              title: "Código",
                              breakpoints: { xs: 24, sm: 12, xl: 2 },
                              dataIndex: "codigo",
                              key: "alergia-codigo",
                              sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                          },
                          {
                              title: "Ativo",
                              breakpoints: { xs: 24, sm: 12, xl: 2 },
                              dataIndex: "ativo",
                              key: "alergia-ativo",
                              sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                              render: (v) => (v == true ? "Sim" : "Não"),
                          },
                          {
                              title: "Nome",
                              breakpoints: { xs: 24, sm: 24, xl: 6 },
                              dataIndex: "nome",
                              key: "alergia-nome",
                              sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                          },
                          {
                              title: "Descrição",
                              breakpoints: { xs: 24, sm: 24, xl: 10 },
                              dataIndex: "descricao",
                              key: "alergia-descricao",
                              sorter: (a, b) => Ordenar.String(a.descricao, b.descricao),
                              render: (v) => v ?? "N/A",
                          },
                          {
                              title: "Ações",
                              key: "acoes",
                              noResponsiveTitle: true,
                              breakpoints: { xs: 24, sm: 24, lg: 4 },
                              render: (_, item) => {
                                  return (
                                      <Conjunto
                                          style={{ justifyContent: breakpoints.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                          itens={[
                                              ...(() => {
                                                  if (item.ativo) {
                                                      return [
                                                          requerPermissoes.todas(Permissoes.AtualizarAlergia) && (
                                                              <Link
                                                                  to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                  onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                              >
                                                                  <Button
                                                                      type="primary"
                                                                      size="small"
                                                                      onClick={() => {
                                                                          props.aoEditar?.(item);
                                                                      }}
                                                                  >
                                                                      Editar
                                                                  </Button>
                                                              </Link>
                                                          ),
                                                          requerPermissoes.todas(Permissoes.DesativarAlergia) && (
                                                              <Popconfirm
                                                                  {...PropriedadesDoPopConfirm("desativar")}
                                                                  onConfirm={() => {
                                                                      if (item.codigo == undefined) return;
                                                                      AlergiaService.desativar(item.codigo)
                                                                          .then(() => {
                                                                              alergiasDisponiveis.refetch();
                                                                          })
                                                                          .catch(() => {});
                                                                  }}
                                                              >
                                                                  <Button type="default" danger size="small">
                                                                      Desativar
                                                                  </Button>
                                                              </Popconfirm>
                                                          ),
                                                      ];
                                                  } else {
                                                      return [
                                                          requerPermissoes.todas(Permissoes.DesativarAlergia) && (
                                                              <Popconfirm
                                                                  {...PropriedadesDoPopConfirm("ativar")}
                                                                  onConfirm={() => {
                                                                      if (item.codigo == undefined) return;

                                                                      AlergiaService.ativar(item.codigo)
                                                                          .then(() => {
                                                                              alergiasDisponiveis.refetch();
                                                                          })
                                                                          .catch(() => {});
                                                                  }}
                                                              >
                                                                  <Button type="dashed" size="small">
                                                                      Reativar
                                                                  </Button>
                                                              </Popconfirm>
                                                          ),
                                                      ];
                                                  }
                                              })(),
                                          ]}
                                      />
                                  );
                              },
                          },
                      ]}
                      
                      csv={{
                          enabled: true,
                          filename: "alergias.csv",
                      }}
                  />
              </Col>
          </Row>
      </React.Fragment>
  );
};

const CadastroDeAlergia: React.FC<{
  aoCancelar?: () => any;
  depoisDeFinalizar?: () => any;
  alergiaEmAlteracao?: TypeAlergia;
}> = (props) => {
  const { alergiaEmAlteracao } = props;
  const requerPermissoes = useRequerPermissoes();

  useEffect(() => {
    if (alergiaEmAlteracao != undefined) {
      form.setFieldsValue(alergiaEmAlteracao);
    }
  }, [alergiaEmAlteracao]);

  const [form] = useForm<TypeAlergia>();

  const inserir = useMutation({
    mutationFn: (payload: TypeAlergia) => AlergiaService.inserir(payload),
    onSuccess() {
      notification.open({
        ...PropriedadesDeNotificacao.Sucesso("Cadastro realizado com sucesso."),
      });

      props.depoisDeFinalizar?.();
    },
  });

  const atualizar = useMutation({
    mutationFn: (payload: TypeAlergia) => AlergiaService.atualizar(payload),
    onSuccess() {
      notification.open({
        ...PropriedadesDeNotificacao.Sucesso("Cadastro atualizado com sucesso."),
      });

      props.depoisDeFinalizar?.();
    },
  });

  const onFinish = (valores: TypeAlergia) => {
    const estaEditando = alergiaEmAlteracao != undefined;
    if (estaEditando) {
      atualizar.mutate({ ...alergiaEmAlteracao, ...valores });
    } else {
      inserir.mutate(valores);
    }
  };

  return (
    <React.Fragment>
      <Form<TypeAlergia> form={form} onFinish={onFinish} layout="vertical" autoComplete="off" validateTrigger={"onBlur"}>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item<TypeAlergia>
              name={"nome"}
              label={"Nome"}
              rules={[
                {
                  required: true,
                  message: "É necessário específicar o nome!",
                },
                {
                  max: 120,
                  message: "O nome não pode exceder 120 caracteres!",
                },
              ]}
            >
              <Input showCount maxLength={120} placeholder="Nome" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item<TypeAlergia>
              name={"descricao"}
              label={"Descrição"}
              normalize={(v: string) => v.trim().length == 0? undefined: v.trim()}
              rules={[
                {
                  max: 400,
                  message: "A descrição não pode exceder 400 caracteres!",
                },
              ]}
            >
              <TextArea showCount rows={5} maxLength={400} style={{ resize: "none" }} placeholder="Aliqua sint cupidatat ad mollit amet duis magna velit enim." />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex align="center" justify="center" gap={16}>
              {props.aoCancelar && (
                <Button
                  danger
                  type="primary"
                  key={"cancel"}
                  onClick={() => {
                    props.aoCancelar && props.aoCancelar();
                  }}
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="primary"
                key={"submit"}
                htmlType="submit"
                disabled={
                  inserir.isPending || atualizar.isPending || props.alergiaEmAlteracao == undefined
                    ? !requerPermissoes.todas(Permissoes.CriarAlergia)
                    : !requerPermissoes.todas(Permissoes.AtualizarAlergia)
                }
              >
                Salvar
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>

      <AdicionarAjuda tutoriais={[]} />
    </React.Fragment>
  );
};

export const Alergia: React.FC<{}> = (props) => {
  const [alergiaEmAlteracao, setAlergiaEmAlteracao] = useState<TypeAlergia | undefined>(undefined);
  const requerPermissoes = useRequerPermissoes();

  const navigate = useNavigate();
  const [raiz, subrota] = useRelativePath();

  const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
  const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

  const obterAlergia = useMutation({
    mutationFn: (codigo: number) => AlergiaService.listar({ codigo, ativo: true }),
    onSuccess: (resposta) => {
      const resultados = resposta.data;
      if (resultados.length == 0) {
        notification.open({
          ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado"),
        });
        navigate(raiz);
      } else {
        setAlergiaEmAlteracao(resposta.data.at(0));
      }
    },
    onError: () => {
      navigate(raiz);
    },
  });

  useRedirection([Permissoes.ListarAlergias, Permissoes.AtualizarAlergia, Permissoes.CriarAlergia, Permissoes.AtivarAlergia, Permissoes.DesativarAlergia]);
  useWatchParameterOnRoutes({
    appliablePaths: [rotaDeEdicao, rotaDeVisualizacao],
    paramToWatch: "id",
    filters: [(p) => isNaN(Number(p))],
    onNotAccepted() {
      navigate(raiz);
    },
    onExists(param) {
      obterAlergia.mutate(Number(param));
    },
  });

  return (
    <>
      <h2 style={{ marginTop: 0 }}>{rotaDeVisualizacao ? "Detalhes da alergia" : rotaDeEdicao ? "Edição da alergia" : "Cadastro de alergia"}</h2>

      <Divider />

      <Routes>
        {
          <Route
            path="*"
            index
            element={
              <ListagemDeAlergias
                hrefDoBotaoAdicionar={raiz + "/cadastro"}
                hrefDoBotaoEditar={(item) => {
                  if (item.codigo) {
                    return raiz + `/editar?id=${item.codigo}`;
                  }
                }}
                aoEditar={(item) => {
                  setAlergiaEmAlteracao(item);
                }}
              />
            }
          />
        }

        {requerPermissoes.todas(Permissoes.CriarAlergia) && (
          <Route
            path="/cadastro"
            element={
              <CadastroDeAlergia
                aoCancelar={() => {
                  navigate(raiz);
                }}
                depoisDeFinalizar={() => {
                  navigate(raiz);
                }}
              />
            }
          />
        )}

        {requerPermissoes.todas(Permissoes.AtualizarAlergia) && (
          <Route
            path="/editar"
            element={
              alergiaEmAlteracao == undefined ? (
                <Skeleton active />
              ) : (
                <CadastroDeAlergia
                  alergiaEmAlteracao={alergiaEmAlteracao}
                  aoCancelar={() => {
                    navigate(raiz);
                  }}
                  depoisDeFinalizar={() => {
                    navigate(raiz);
                  }}
                />
              )
            }
          />
        )}
      </Routes>
    </>
  );
};

export default Alergia;
